import { ApiFailureSchema } from '@trellis/shared'
import type {
  ApiPagedSuccess,
  ApiSuccess,
  RequestPagedResult,
  RequestResult,
} from '@trellis/shared'

/**
 * Unwraps the ApiSuccess<T> envelope returned by the backend's ResponseInterceptor and
 * normalises failures into RequestResult<T>.
 *
 * The generated apiClient returns HttpResponse<DTO> per the OpenAPI spec types, but at
 * runtime the backend wraps every success body in { status, data: DTO, pagination: null,
 * error: null }. This function handles that mismatch so callers always receive the typed
 * result directly.
 *
 * On non-2xx responses the generated client throws the HttpResponse object; the .error
 * property contains the ApiFailure body written by GlobalExceptionFilter.
 */
export async function request<T>(
  fn: () => Promise<{ data: unknown }>,
): Promise<RequestResult<T>> {
  try {
    const response = await fn()
    // 204 No Content — no envelope body
    if (!response.data) {
      return { status: 204, data: undefined as T, pagination: null, error: null }
    }
    const envelope = response.data as ApiSuccess<T>
    return { status: envelope.status, data: envelope.data, pagination: null, error: null }
  } catch (caughtError) {
    const parsedFailure = ApiFailureSchema.safeParse(
      (caughtError as { error?: unknown }).error,
    )
    if (parsedFailure.success) {
      return {
        status: parsedFailure.data.status,
        data: null,
        pagination: null,
        error: parsedFailure.data.error,
      }
    }
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'An unexpected error occurred',
      },
    }
  }
}

/**
 * Unwraps the ApiPagedSuccess<T> envelope returned by the backend's ResponseInterceptor
 * for paginated endpoints that return PagedResult<T>.
 */
export async function requestPaged<T>(
  fn: () => Promise<{ data: unknown }>,
): Promise<RequestPagedResult<T>> {
  try {
    const response = await fn()
    const envelope = response.data as ApiPagedSuccess<T>
    return {
      status: envelope.status,
      data: envelope.data,
      pagination: envelope.pagination,
      error: null,
    }
  } catch (caughtError) {
    const parsedFailure = ApiFailureSchema.safeParse(
      (caughtError as { error?: unknown }).error,
    )
    if (parsedFailure.success) {
      return {
        status: parsedFailure.data.status,
        data: null,
        pagination: null,
        error: parsedFailure.data.error,
      }
    }
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'An unexpected error occurred',
      },
    }
  }
}
